import { useState, useRef, useEffect } from "react"
import { Check, ChevronDown, Search, X } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

const MultiSelect = ({ options, value = [], onChange, placeholder, icon, defaultValue = [] }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedValues, setSelectedValues] = useState([])
  const dropdownRef = useRef(null)

  // Initialize selected values when component mounts or when defaultValue/value changes
  useEffect(() => {
    // If we have values in the value prop, use those
    if (Array.isArray(value) && value.length > 0) {
      // If value contains objects with value property, extract just the values
      if (typeof value[0] === "object" && value[0]?.value) {
        setSelectedValues(value.map((item) => item.value))
      } else {
        setSelectedValues(value)
      }
    }
    // Otherwise use defaultValue if provided
    else if (Array.isArray(defaultValue) && defaultValue.length > 0) {
      // If defaultValue contains objects with value property, extract just the values
      if (typeof defaultValue[0] === "object" && defaultValue[0]?.value) {
        setSelectedValues(defaultValue.map((item) => item.value))
      } else {
        setSelectedValues(defaultValue)
      }
    }
  }, [value, defaultValue])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Filter options based on search term
  const filteredOptions = options.filter((option) => option.name.toLowerCase().includes(searchTerm.toLowerCase()))

  // Get selected options
  const selectedOptions = options.filter((option) => selectedValues.includes(option.value))

  // Handle selection toggle
  const toggleOption = (optionValue) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue]

    setSelectedValues(newValues)

    // If the original value was an array of objects, maintain that format
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object") {
      const newValueObjects = options.filter((option) => newValues.includes(option.value))
      onChange(newValueObjects)
    } else {
      onChange(newValues)
    }
  }

  // Remove a selected option
  const removeOption = (e, optionValue) => {
    e.stopPropagation()
    const newValues = selectedValues.filter((v) => v !== optionValue)
    setSelectedValues(newValues)

    // If the original value was an array of objects, maintain that format
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object") {
      const newValueObjects = options.filter((option) => newValues.includes(option.value))
      onChange(newValueObjects)
    } else {
      onChange(newValues)
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
        className="w-full flex items-center justify-between px-4 py-2 min-h-10 border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-600 transition-all"
      >
        <div className="flex items-center gap-2 flex-wrap">
          {icon && <span className="text-neutral-500 dark:text-neutral-400">{icon}</span>}

          {selectedOptions.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {selectedOptions.map((option) => (
                <div
                  key={option.id}
                  className="flex items-center gap-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded text-xs"
                >
                  <span className="truncate max-w-[150px]">{option.name}</span>
                  <button
                    onClick={(e) => removeOption(e, option.value)}
                    className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-200"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-xs text-neutral-500 dark:text-neutral-400">{placeholder}</span>
          )}
        </div>
        <ChevronDown
          size={16}
          className={`text-neutral-500 dark:text-neutral-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
            }`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 mt-1 w-full bg-white dark:border-neutral-900 dark:bg-neutral-900 border border-neutral-200 dark:bg-neutral-800 rounded-xl shadow-lg overflow-hidden"
            style={{ minWidth: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" />
                <input
                  type="text"
                  className="w-full h-10 pl-9 pr-3 text-xs bg-neutral-50 dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-600 focus:border-transparent text-neutral-900 dark:text-neutral-200"
                  placeholder={`Search ${placeholder.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto px-2 py-1">
              {filteredOptions.map((option) => (
                <button
                  key={option.id}
                  className={`w-full text-left px-4 py-2 rounded-xl text-xs hover:bg-neutral-50 dark:hover:bg-neutral-700 ${selectedValues.includes(option.value)
                    ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                    : "text-neutral-700 dark:text-neutral-300"
                    }`}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleOption(option.value)
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span>{option.name}</span>
                    {selectedValues.includes(option.value) && (
                      <Check size={16} className="text-blue-600 dark:text-blue-400" />
                    )}
                  </div>
                </button>
              ))}
              {filteredOptions.length === 0 && (
                <div className="px-4 py-2 text-xs text-neutral-500 dark:text-neutral-400">No results found</div>
              )}
            </div>
            {selectedValues.length > 0 && (
              <div className="p-2 border-t border-neutral-200 dark:bg-neutral-800">
                <button
                  className="w-full text-xs text-center py-1.5 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                  onClick={() => {
                    setSelectedValues([])
                    onChange([])
                  }}
                >
                  Clear all
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default MultiSelect
